import { useMemo } from "react";
import { create } from "zustand";
import { nativeApp } from "./lib/androidApp";
import {
  COLLECTIONS, DEFAULT_SETTINGS, DomainError, addDays, colomboBusinessDate, hasPermission,
  type CollectionName, type CollectionTypes, type CommandName, type CommandResult, type Permission, type Settings, type User
} from "@mango-moons/domain";
import { temporaryPassword, type Backend, type Change } from "./backend/types";
import type { LocalBackend } from "./backend/local";
import { activeCloudConfig } from "./lib/cloudConfig";
import { desktop } from "./lib/desktop";
import { DEMO } from "./lib/demoGuard";

export type Screen =
  | "dashboard" | "overview" | "sale" | "orders" | "tables" | "kitchen" | "menu" | "promotions" | "complimentary" | "customers" | "inventory"
  | "expenses" | "shifts" | "reports" | "staff" | "printers" | "updates" | "settings" | "audit" | "attendance";
export type Data = { [C in CollectionName]: CollectionTypes[C][] };
export type Toast = { id: number; kind: "success" | "error" | "info"; message: string };
/** Context handed to the sale screen: a table to seat, or an open bill to add items to. */
export type SaleIntent = { tableId?: string; orderId?: string } | null;

const emptyData = () => Object.fromEntries(COLLECTIONS.map((name) => [name, []])) as unknown as Data;
/** How many days of dated records stay live in memory; older history is fetched on demand. */
export const LIVE_DAYS = 14;

type AppState = {
  status: "loading" | "setup" | "signed-out" | "ready" | "failed";
  error: string; mode: Backend["mode"]; user: User | null; data: Data; screen: Screen; saleIntent: SaleIntent;
  openOrderId: string | null; toasts: Toast[]; online: boolean;
};
export const useApp = create<AppState>(() => ({
  status: "loading", error: "", mode: "local", user: null, data: emptyData(), screen: "dashboard", saleIntent: null,
  openOrderId: null, toasts: [], online: navigator.onLine
}));

let backend: Backend;
let unsubscribe: (() => void) | undefined;
export const getBackend = () => backend;

function apply(changes: Change[]) {
  dirty = true;
  useApp.setState((state) => {
    const data = { ...state.data };
    const touched = new Map<CollectionName, Map<string, unknown>>();
    for (const { collection, doc } of changes) {
      let map = touched.get(collection);
      if (!map) touched.set(collection, map = new Map((state.data[collection] as { id: string }[]).map((entry) => [entry.id, entry])));
      // A record staff deleted (dish, category, stock item, supplier) disappears from every screen; history keeps it.
      if ((doc as { removed?: boolean }).removed) map.delete(doc.id); else map.set(doc.id, doc);
    }
    for (const [collection, map] of touched) (data as Record<string, unknown[]>)[collection] = [...map.values()];
    const self = state.user && touched.get("users")?.get(state.user.id) as User | undefined;
    return self ? { data, user: self } : { data };
  });
}

/** Adds history fetched on demand to the in-memory data so detail views can open it. */
export function mergeFetched<C extends CollectionName>(collection: C, docs: readonly CollectionTypes[C][]) {
  apply(docs.map((doc) => ({ collection, doc: doc as unknown as Change["doc"] })));
}

/** Which account this phone's alerts belong to (the device id only - the secret token stays in the Android app). */
const ALERTS_KEY = "mm.alerts.link";
const readLink = (): { userId: string; deviceId: string } | null => { try { return JSON.parse(localStorage.getItem(ALERTS_KEY) ?? "null"); } catch { return null; } };
const writeLink = (link: { userId: string; deviceId: string } | null) => { try { if (link) localStorage.setItem(ALERTS_KEY, JSON.stringify(link)); else localStorage.removeItem(ALERTS_KEY); } catch { /* not remembered */ } };

/**
 * Inside the Android app: lets this phone receive management alerts as notifications - only while a manager is signed
 * in, and only for that manager. A phone still linked to someone else (or to nobody known) is unlinked first.
 */
async function linkPhoneAlerts(user: User) {
  try {
    if (!nativeApp || backend.mode !== "firebase") return;
    const link = readLink(), allowed = !user.mustChangePassword && hasPermission(user.role, "reports.read", user.permissions);
    if (nativeApp.hasDevice() && link?.userId === user.id && allowed) return;
    if (nativeApp.hasDevice()) { nativeApp.clearDevice(); writeLink(null); }
    if (!allowed) return;
    const { device, since } = await backend.call("device.register", { label: "Android phone" });
    nativeApp.setDevice(device, since);
    writeLink({ userId: user.id, deviceId: device.split(".")[0]! });
  } catch { /* tried again at the next sign-in or app start */ }
}

/**
 * Everyone is signed out after midnight (Sri Lanka time), even if they forgot: the next day starts with a fresh sign-in.
 * Checked every half minute and when the computer wakes, so a sleeping computer is signed out as soon as it wakes.
 * Not on the managers' phone app, which must stay signed in to receive the day-end report.
 */
const SESSION_DAY_KEY = "mm.session.day";
const colomboDay = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Colombo" }).format(new Date());
let midnightTimer: ReturnType<typeof setInterval> | undefined, midnightCheck: (() => void) | undefined;
function watchMidnight(started: boolean) {
  if (nativeApp) return;
  let day: string | null = null;
  try { if (started) localStorage.setItem(SESSION_DAY_KEY, colomboDay()); day = localStorage.getItem(SESSION_DAY_KEY); } catch { day = colomboDay(); }
  day ??= colomboDay();
  const check = () => { if (useApp.getState().user && colomboDay() !== day) void signOut().then(() => toast("info", "Signed out after midnight. Please sign in for today.")); };
  clearInterval(midnightTimer);
  midnightCheck = check;
  midnightTimer = setInterval(check, 30_000);
  check();
}
addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") midnightCheck?.(); });

/** `fresh`: a real sign-in now (not a session restored at start-up), so the day of the session is today. */
function startSession(user: User, fresh = true) {
  unsubscribe?.();
  void linkPhoneAlerts(user);
  watchMidnight(fresh);
  useApp.setState({ user, data: emptyData(), status: "ready", screen: hasPermission(user.role, "orders.create", user.permissions) ? "dashboard" : hasPermission(user.role, "reports.read", user.permissions) ? "overview" : "kitchen" });
  if (!user.mustChangePassword) unsubscribe = backend.subscribe(user, addDays(colomboBusinessDate(new Date()), -LIVE_DAYS), apply);
}

let dirty = false;
/**
 * Local mode on the desktop: writes a dated backup file through the main process shortly after
 * start-up and then every 20 minutes while data is changing. The shell keeps the last 14 days.
 */
function scheduleAutoBackup(local: LocalBackend) {
  const write = async () => {
    if (!dirty) return;
    dirty = false;
    try { await desktop!.backup.writeAuto(await local.snapshot()); }
    catch (error) { dirty = true; console.warn("Automatic backup failed", error); }
  };
  dirty = true;
  setTimeout(() => void write(), 30_000);
  setInterval(() => void write(), 20 * 60_000);
}

/**
 * Cloud mode on the desktop: every hour this computer writes what it has synced (no extra database reads) to an
 * encrypted file, so a cloud outage, a lost account or a mistake does not take the only copy. The file is per day and
 * each hour replaces it with the fresh data; 14 days are kept (and copied to the second backup folder, if chosen).
 * Nothing is written until a backup password is set: an unencrypted copy of the restaurant's data must not sit on disk.
 */
const CLOUD_COPY_MS = 60 * 60_000;
function scheduleCloudCopy() {
  const write = async () => {
    const { user, data } = useApp.getState();
    if (!user || !hasPermission(user.role, "settings.manage", user.permissions) && !hasPermission(user.role, "orders.create", user.permissions)) return;
    if (data.settings.length === 0) return; // not synced yet
    const { BACKUP_FORMAT, encryptBackup, storedBackupKey } = await import("./backend/backupCrypto");
    const key = await storedBackupKey();
    if (!key) return;
    try {
      const plain = JSON.stringify({ format: BACKUP_FORMAT, version: 1, source: "cloud-copy", exportedAt: new Date().toISOString(), collections: data });
      await desktop!.backup.writeAuto(await encryptBackup(plain, key));
    } catch (error) { console.warn("Hourly cloud copy failed", error); }
  };
  setTimeout(() => void write(), 3 * 60_000);
  setInterval(() => void write(), CLOUD_COPY_MS);
}

export const INITIAL_ADMIN_LOGIN = "admin";
/**
 * Desktop first launch: creates the single Super Admin with a password generated on this computer
 * and hands it over in a file. Nothing is baked into the installer, and the account must choose a
 * new password at first sign-in. Falls back to the manual setup screen if the file cannot be written.
 */
async function createInitialAdmin(): Promise<boolean> {
  try {
    const password = temporaryPassword();
    await desktop!.credentials.write({ login: INITIAL_ADMIN_LOGIN, password });
    try { await backend.bootstrap!({ email: INITIAL_ADMIN_LOGIN, displayName: "Administrator", password, mustChangePassword: true }); }
    catch (error) { await desktop!.credentials.remove(); throw error; }
    return true;
  } catch (error) {
    console.warn("Automatic administrator setup failed", error);
    return false;
  }
}

export async function initApp() {
  try {
    if (DEMO) {
      // Sample data in memory: no cloud, no backups, nothing saved (lib/demoGuard.ts, backend/demo.ts).
      backend = new (await import("./backend/demo")).DemoBackend();
      const { user } = await backend.init();
      useApp.setState({ mode: backend.mode });
      startSession(user!, false);
      return;
    }
    const firebaseReady = Boolean(activeCloudConfig());
    backend = firebaseReady ? new (await import("./backend/firebase")).FirebaseBackend() : new (await import("./backend/local")).LocalBackend();
    const { user, needsSetup } = await backend.init();
    if (backend.mode === "local" && desktop) scheduleAutoBackup(backend as LocalBackend);
    if (backend.mode === "firebase" && desktop?.backup) scheduleCloudCopy();
    useApp.setState({ mode: backend.mode });
    if (needsSetup && backend.mode === "local" && desktop?.credentials && await createInitialAdmin()) useApp.setState({ status: "signed-out" });
    else if (needsSetup) useApp.setState({ status: "setup" });
    else if (user) startSession(user, false);
    else useApp.setState({ status: "signed-out" });
  } catch (error) {
    console.error(error);
    useApp.setState({ status: "failed", error: error instanceof Error ? error.message : "The application could not start." });
  }
  addEventListener("online", () => useApp.setState({ online: true }));
  addEventListener("offline", () => useApp.setState({ online: false }));
}

export async function signIn(login: string, password: string, code?: string) { startSession(await backend.signIn(login, password, code)); }
export async function recoverAccount(login: string, code: string, newPassword: string) { startSession(await backend.recoverAccount!(login, code, newPassword)); }
export async function signInWithRecovery(login: string, password: string, recovery: string) { startSession(await backend.signInWithRecovery!(login, password, recovery)); }
export async function signOut() {
  unsubscribe?.(); unsubscribe = undefined;
  clearInterval(midnightTimer); midnightTimer = undefined; midnightCheck = undefined;
  // The phone stops getting management alerts the moment its manager signs out (on the server too, not just here).
  const link = readLink();
  if (nativeApp && link) await backend.call("device.revoke", { id: link.deviceId }).catch(() => undefined);
  writeLink(null);
  try { nativeApp?.clearDevice(); } catch { /* older app without notifications */ }
  await backend.signOut();
  useApp.setState({ user: null, data: emptyData(), status: "signed-out", saleIntent: null, openOrderId: null });
}
export async function completeSetup(input: { email: string; displayName: string; password: string }) {
  await backend.bootstrap!(input);
  await signIn(input.email, input.password);
}
export async function changePassword(current: string, next: string) {
  const user = await backend.changePassword(current, next);
  // The temporary password no longer works, so its hand-over file must not linger.
  if (user.email === INITIAL_ADMIN_LOGIN) await desktop?.credentials?.remove().catch(() => undefined);
  startSession(user);
}

export const navigate = (screen: Screen, saleIntent: SaleIntent = null) => useApp.setState({ screen, saleIntent });
export const openOrder = (orderId: string | null) => useApp.setState({ openOrderId: orderId });

let toastId = 0;
export function toast(kind: Toast["kind"], message: string) {
  const id = ++toastId;
  useApp.setState((state) => ({ toasts: [...state.toasts, { id, kind, message }] }));
  setTimeout(() => useApp.setState((state) => ({ toasts: state.toasts.filter((entry) => entry.id !== id) })), kind === "error" ? 7000 : 3500);
}
export const errorMessage = (error: unknown) => error instanceof DomainError ? error.message : error instanceof Error ? error.message : "Something went wrong.";

export function api<N extends CommandName>(name: N, input: unknown): Promise<CommandResult<N>> { return backend.call(name, input); }
/** Runs an action, reports failure as a toast and resolves to `undefined` instead of throwing. */
export async function attempt<T>(work: () => Promise<T>, success?: string): Promise<T | undefined> {
  try {
    const result = await work();
    if (success) toast("success", success);
    return result;
  } catch (error) {
    toast("error", errorMessage(error));
    return undefined;
  }
}

export const useData = <C extends CollectionName>(collection: C) => useApp((state) => state.data[collection]);
export const useUser = () => useApp((state) => state.user)!;
/** Stored settings completed with defaults, so a field or role added in a later version is never missing. */
export const useSettings = (): Settings => {
  const stored = useApp((state) => state.data.settings[0]);
  return useMemo(() => stored ? { ...DEFAULT_SETTINGS, ...stored, thresholds: { ...DEFAULT_SETTINGS.thresholds, ...stored.thresholds } } : DEFAULT_SETTINGS, [stored]);
};
export const useCan = () => {
  const user = useApp((state) => state.user);
  return (permission: Permission) => Boolean(user && hasPermission(user.role, permission, user.permissions));
};
export const today = () => colomboBusinessDate(new Date());

// Development-only handle for seeding and inspecting data from the browser console.
if (import.meta.env.DEV) (window as unknown as Record<string, unknown>).__mm = { api, getBackend, useApp, completeSetup, navigate, openOrder };
